import { License, EnrichedLicense, Company, Project } from "../store/types";
import { getLicenseKindLabel } from "./useLicenseType";
import {
  getValidityDays,
  getRemainingDays,
} from "../components/DatatableTemplates/LicenseTableTemplates";

interface EnrichOptions {
  projects?: Project[] | null;
  companies?: Company[] | null;
  projectName?: string | null;
  organizationName?: string | null;
}

/**
 * Дополняет лицензию v2 полями, которые нужны таблицам:
 * подпись вида лицензии, названия проекта/компании и вычисленные сроки.
 */
export const enrichLicense = (
  license: License,
  options: EnrichOptions = {}
): EnrichedLicense => {
  const { projects, companies, projectName, organizationName } = options;

  return {
    ...license,
    kindLabel: getLicenseKindLabel(license.kind),
    projectName:
      license.projectName ??
      projectName ??
      projects?.find((p) => p.id === license.projectId)?.name ??
      license.projectId,
    organizationName:
      license.organizationName ??
      organizationName ??
      companies?.find((c) => c.id === license.organizationId)?.name ??
      String(license.organizationId ?? ""),
    _validityDays: getValidityDays(license),
    _timeRemainingDays: getRemainingDays(license),
  };
};

/** Порог, с которого лицензия считается истекающей (в днях) */
const EXPIRING_DAYS = 30;

export interface LicenseStats {
  total: number;
  active: number;
  expiring: number;
  inactive: number;
  /** суммарное количество пользователей онлайн по лицензиям */
  online: number;
}

/** Категории лицензии, соответствующие карточкам статистики */
const classifyLicense = (license: License) => {
  const daysRemaining = getRemainingDays(license);
  const isActive = license.isEnabled && daysRemaining >= 0;

  return {
    isActive,
    isExpiring:
      isActive && isFinite(daysRemaining) && daysRemaining < EXPIRING_DAYS,
    isInactive: !isActive,
    isOnline: (license.onlineCount ?? 0) > 0,
  };
};

/** Сводка по списку лицензий для карточек статистики */
export const getLicenseStats = (
  licenses: License[] | null | undefined
): LicenseStats => {
  if (!licenses)
    return { total: 0, active: 0, expiring: 0, inactive: 0, online: 0 };

  let active = 0;
  let expiring = 0;
  let inactive = 0;
  let online = 0;

  licenses.forEach((license) => {
    const { isActive, isExpiring, isInactive } = classifyLicense(license);

    if (isActive) active++;
    if (isExpiring) expiring++;
    if (isInactive) inactive++;

    online += license.onlineCount ?? 0;
  });

  return { total: licenses.length, active, expiring, inactive, online };
};

/** Фильтр таблицы лицензий, соответствующий карточке статистики */
export type LicenseStatFilter =
  | "all"
  | "active"
  | "expiring"
  | "inactive"
  | "online";

/** Попадает ли лицензия в выбранную карточкой статистики категорию */
export const matchesLicenseStat = (
  license: License,
  filter: LicenseStatFilter
): boolean => {
  if (filter === "all") return true;

  const { isActive, isExpiring, isInactive, isOnline } =
    classifyLicense(license);

  switch (filter) {
    case "active":
      return isActive;
    case "expiring":
      return isExpiring;
    case "inactive":
      return isInactive;
    case "online":
      return isOnline;
    default:
      return true;
  }
};

/** Строка для поиска по лицензии в таблицах */
export const licenseSearchText = (license: EnrichedLicense): string =>
  [
    license.projectName,
    license.organizationName,
    license.kindLabel,
    license.comments,
    license.isEnabled ? "активирована" : "деактивирована",
    license.isActive ? "действует" : "не действует",
    license.startsAt,
    license.expiresAt,
    license.issuedAt,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
